import React, { useEffect, useState, useRef } from 'react';
import {
  bool, func, number, string,
} from 'prop-types';
import classnames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from '../Icon/Icon';
import { useSetLikeMutation, useDeleteLikeMutation } from '../../services/likesApi';
import styles from './HeartLikes.module.scss';
import { useLazyGetUserQuery } from '../../services/authApi';
import Button from '../Button/Button';
import useMediaQuery from '../../hooks/useMediaQuery';
import ModalCloseButton from '../Modal/ModalCloseButton';
import LikeModal from '../LikeModal/LikeModal';
import useModal from '../../hooks/useModal';
import useOutsideClick from '../../hooks/useOutsideClick';

const HeartLikes = ({
  likes,
  type,
  id,
  userId,
  disabled,
  isLiked,
  switchLikes,
  currentlyLikedIdeaId,
  currentLikeId,
  hasNoLikes,
}) => {
  const [setLike, { isLoading: isLoadingSetLike }] = useSetLikeMutation();
  const [deleteLike, { isLoading: isLoadingDeleteLike }] = useDeleteLikeMutation();
  const [triggerGetUser] = useLazyGetUserQuery();

  const confirmPopupRef = useRef(null);

  const isMobile = useMediaQuery('(max-width: 767px)');

  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);

  const {
    isOpen,
    openModalHandler,
    closeModalHandler,
  } = useModal();

  useEffect(() => {
    if (isLoadingSetLike || isLoadingDeleteLike) {
      setIsMouseOver(false);
    }
  }, [isLoadingSetLike, isLoadingDeleteLike]);

  const changeHeartHandle = async () => {
    if (disabled) return;

    const data = {
      id: currentLikeId,
      userId,
      likeableId: id,
      likeableType: type,
    };

    setIsConfirmPopupOpen(false);

    try {
      let resultOfAdding;

      if (currentlyLikedIdeaId) {
        await deleteLike(data.id).unwrap();
      }

      if (currentlyLikedIdeaId !== id) {
        resultOfAdding = await setLike(data).unwrap();
      }

      switchLikes(currentlyLikedIdeaId, id, resultOfAdding && resultOfAdding.data.id);
      triggerGetUser();
    } catch (error) {
      console.log('error', error);
    }
  };

  const toggleConfirmPopup = () => {
    setIsConfirmPopupOpen(!isConfirmPopupOpen);
  };

  const determineLikeHandler = () => {
    if (isLiked || hasNoLikes) return changeHeartHandle;

    return isMobile ? openModalHandler : toggleConfirmPopup;
  };

  const scrollIntoViewHandler = () => {
    const element = document.getElementById(`${currentlyLikedIdeaId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setIsConfirmPopupOpen(false);
  };

  useOutsideClick(confirmPopupRef, () => {
    if (confirmPopupRef.current && isConfirmPopupOpen) {
      setIsConfirmPopupOpen(false);
    }
  });

  return (
    <>
      <div className={classnames(
        styles.container,
        'HeartLikes-container',
        {
          [styles.loaderContainer]: isLoadingSetLike || isLoadingDeleteLike,
          [styles.disabled]: disabled,
        },
      )}
      >
        {isMobile ? (
          <LikeModal
            isOpen={isOpen}
            onClose={closeModalHandler}
            onConfirm={changeHeartHandle}
          />
        ) : (
          <motion.div
            ref={confirmPopupRef}
            animate={{
              opacity: isConfirmPopupOpen ? 1 : 0,
              translateY: isConfirmPopupOpen ? 0 : 20,
            }}
            style={{
              visibility: isConfirmPopupOpen ? 'visible' : 'hidden',
              pointerEvents: isConfirmPopupOpen ? 'all' : 'none',
            }}
            transition={{ duration: 0.3 }}
            className={styles.confirmPopup}
          >
            <div className={styles.textContent}>
              <p>
                Вы уже поддержали другую идею.
                {' '}
                <br />
                {' '}
                Хотите изменить выбор?
              </p>
            </div>
            <div className={styles.buttons}>
              <Button
                onClick={changeHeartHandle}
                className={styles.confirmButton}
                typeButton="button-fill"
              >
                Да
              </Button>
              {
              !isLiked && (
              <button
                type="button"
                onClick={scrollIntoViewHandler}
                className={styles.link}
              >
                <div className={styles.name}>
                  {isMobile ? 'Показать' : 'Показать выбранную идею'}
                </div>
              </button>
              )
            }
            </div>
            <ModalCloseButton
              className={styles.closeBtn}
              showOnDesktop
              onClick={toggleConfirmPopup}
            />
          </motion.div>
        )}
        <AnimatePresence>
          {isMouseOver && !isConfirmPopupOpen && !isLiked && !hasNoLikes && (
          <motion.div
            initial={{ opacity: 0, translateY: 20 }}
            animate={{ opacity: 1, translateY: 0 }}
            transition={{ duration: 0.3 }}
            className={styles.warningPopup}
          >
            Вы уже поддержали другую идею. Нужно выбрать что-то одно.
          </motion.div>
          )}
        </AnimatePresence>

        {isLoadingSetLike || isLoadingDeleteLike ? (<div className={styles.loader} />)
          : (
            <>
              {likes > 0 ? likes : ''}
              <button
                disabled={disabled || isLoadingSetLike || isLoadingDeleteLike}
                onMouseEnter={() => setIsMouseOver(true)}
                onMouseLeave={() => setIsMouseOver(false)}
                type="button"
                className={classnames(
                  styles.button,
                  {
                    [styles.like]: isLiked && !disabled,
                  },
                )}
                onClick={determineLikeHandler()}
              >
                <Icon name="heart" className={styles.svg} />
              </button>
            </>
          )}
      </div>
    </>
  );
};

HeartLikes.propTypes = {
  likes: number.isRequired,
  type: string.isRequired,
  id: number.isRequired,
  userId: number.isRequired,
  disabled: bool,
  isLiked: bool.isRequired,
  switchLikes: func.isRequired,
  currentlyLikedIdeaId: number,
  currentLikeId: number,
  hasNoLikes: bool.isRequired,
};

HeartLikes.defaultProps = {
  currentlyLikedIdeaId: null,
  disabled: true,
  currentLikeId: null,
};

export default HeartLikes;
